import React, { useState, useEffect } from 'react';
import axios from 'axios';
import KBCMultiplexChallenge from './KBCMultiplexChallenge';
import KBCChallengeReceiver from './KBCChallengeReceiver';
import './QuestionManager.css';
import './KBCMultiplex.css';

const TeacherQuestionSelector = ({ 
  userRole, 
  onStartGame, 
  onBack,
  userName 
}) => {
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [filters, setFilters] = useState({
    subject: '',
    chapter: '',
    class: '',
    difficulty: '',
    source: 'both'
  });
  const [subjects, setSubjects] = useState([]);
  const [questionPapers, setQuestionPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState('manual'); // 'manual' or 'paper'
  const [selectedPaper, setSelectedPaper] = useState('');
  const [showChallengeScreen, setShowChallengeScreen] = useState(false);

  // Check if user is teacher
  const isTeacher = ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(userRole);

  useEffect(() => {
    if (isTeacher) {
      fetchSubjects();
      fetchQuestionPapers();
    }
  }, [isTeacher]);

  useEffect(() => {
    if (isTeacher && selectionMode === 'manual') {
      fetchQuestions();
    }
  }, [filters, isTeacher, selectionMode]);

  const fetchSubjects = async () => {
    try {
      const response = await axios.get('/api/subjects');
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchQuestionPapers = async () => {
    try {
      const response = await axios.get('/api/quiz/question-papers', {
        params: { assignedTeacher: userName }
      });
      setQuestionPapers(response.data);
    } catch (error) {
      console.error('Error fetching question papers:', error);
    }
  };

  const fetchQuestions = async () => {
    if (!filters.subject && !filters.class) return;
    
    setLoading(true);
    try {
      const params = {
        ...filters,
        limit: 50 // Get more questions for selection
      };
      
      const response = await axios.get('/api/quiz/questions', { params });
      setAvailableQuestions(response.data || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setAvailableQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionToggle = (question) => {
    const isSelected = selectedQuestions.find(q => q._id === question._id);
    
    if (isSelected) {
      setSelectedQuestions(selectedQuestions.filter(q => q._id !== question._id));
    } else if (selectedQuestions.length < 12) {
      setSelectedQuestions([...selectedQuestions, question]);
    } else {
      alert('Maximum 12 questions allowed for KBC Multiplex Mode');
    }
  };

  const handleSelectFromPaper = async () => {
    if (!selectedPaper) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`/api/quiz/question-papers/${selectedPaper}/questions`, {
        params: { limit: 12 }
      });
      
      if (response.data && response.data.length >= 12) {
        setSelectedQuestions(response.data.slice(0, 12));
        alert(`Selected 12 questions from the question paper`);
      } else {
        alert('This question paper does not have enough questions (minimum 12 required)');
      }
    } catch (error) {
      console.error('Error fetching paper questions:', error);
      alert('Error loading questions from paper');
    } finally {
      setLoading(false);
    }
  };

  const handleStartMultiplexGame = (multiplayer = false) => {
    if (selectedQuestions.length < 12) {
      alert('Please select exactly 12 questions for KBC Multiplex Mode');
      return;
    }

    if (multiplayer) {
      // Show challenge screen for opponent selection
      setShowChallengeScreen(true);
      return;
    }

    const gameConfig = {
      mode: 'kbc-multiplex',
      subject: filters.subject || 'Mixed',
      class: filters.class || 'All',
      difficulty: filters.difficulty || 'Mixed',
      questionCount: 12,
      timeLimit: false,
      customQuestions: true,
      multiplayer: false
    };

    onStartGame({
      mode: 'kbc-multiplex',
      config: gameConfig,
      questions: selectedQuestions
    });
  };

  const handleStartTeacherStudentGame = () => {
    if (selectedQuestions.length < 12) {
      alert('Please select exactly 12 questions for Teacher-Student KBC Mode');
      return;
    }

    const gameConfig = {
      mode: 'teacher-student-kbc',
      subject: filters.subject || 'Mixed',
      class: filters.class || 'All',
      difficulty: filters.difficulty || 'Mixed',
      questionCount: 12,
      timeLimit: false,
      customQuestions: true,
      teacherControlled: true
    };

    onStartGame({
      mode: 'teacher-student-kbc',
      config: gameConfig,
      questions: selectedQuestions
    });
  };

  const clearSelection = () => {
    setSelectedQuestions([]);
  };

  if (!isTeacher) {
    return (
      <div className="question-manager">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only teachers can access the KBC Multiplex Mode question selector.</p>
          <button onClick={onBack} className="back-btn">Back</button>
        </div>
      </div>
    );
  }

  if (showChallengeScreen) {
    return (
      <KBCMultiplexChallenge
        userRole={userRole}
        userName={userName}
        selectedQuestions={selectedQuestions}
        onStartGame={onStartGame}
        onBack={() => setShowChallengeScreen(false)}
      />
    );
  }

  return (
    <div className="question-manager teacher-selector-container">
      {/* Challenge Receiver - shows incoming challenges */}
      <KBCChallengeReceiver 
        userName={userName}
        onChallengeAccepted={onStartGame}
      />
      <div className="manager-header selector-header">
        <h2 className="selector-title">🎮 KBC Multiplex Mode - Question Selector</h2>
        <div className="header-actions">
          <button 
            onClick={() => handleStartMultiplexGame(false)}
            className="add-btn"
            disabled={selectedQuestions.length !== 12}
            style={{
              background: selectedQuestions.length === 12 ? '#10b981' : '#ccc',
              cursor: selectedQuestions.length === 12 ? 'pointer' : 'not-allowed'
            }}
          >
            Single Player ({selectedQuestions.length}/12)
          </button>
          <button 
            onClick={() => handleStartMultiplexGame(true)}
            className="bulk-btn"
            disabled={selectedQuestions.length !== 12}
            style={{
              background: selectedQuestions.length === 12 ? '#8b5cf6' : '#ccc',
              cursor: selectedQuestions.length === 12 ? 'pointer' : 'not-allowed'
            }}
          >
            Multiplayer ({selectedQuestions.length}/12)
          </button>
          <button 
            onClick={() => handleStartTeacherStudentGame()}
            className="update-btn"
            disabled={selectedQuestions.length !== 12}
            style={{
              background: selectedQuestions.length === 12 ? '#f59e0b' : '#ccc',
              cursor: selectedQuestions.length === 12 ? 'pointer' : 'not-allowed'
            }}
          >
            Teacher-Student ({selectedQuestions.length}/12)
          </button>
          <button onClick={clearSelection} className="bulk-btn">
            Clear Selection
          </button>
          <button onClick={onBack} className="back-btn">
            Back
          </button>
        </div>
      </div>

      <div className="feature-info-box">
        <h3 className="feature-info-title">🎯 KBC Multiplex Mode Features:</h3>
        <ul className="feature-list">
          <li>Select exactly 12 questions of the same type</li>
          <li>No time limit - questions remain active until answered</li>
          <li>Timer sound plays in background for ambiance</li>
          <li>Built-in customizable feature for teachers</li>
          <li>All standard KBC lifelines available</li>
        </ul>
      </div>

      {selectedQuestions.length === 0 && (
        <div style={{ 
          background: '#fef3c7', 
          border: '2px solid #f59e0b', 
          borderRadius: '8px', 
          padding: '15px', 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#92400e', fontWeight: 'bold', marginBottom: '8px' }}>📋 Quick Start Guide:</div>
          <div style={{ color: '#78350f', fontSize: '14px' }}>
            1. Choose a <strong>Subject</strong> and <strong>Class</strong> from filters below<br/>
            2. Click on <strong>12 questions</strong> to select them (they'll turn green)<br/>
            3. Game buttons will become active when 12 questions are selected
          </div>
        </div>
      )}

      {/* Selection Mode Toggle */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button
            onClick={() => setSelectionMode('manual')}
            style={{
              padding: '8px 16px',
              background: selectionMode === 'manual' ? '#10b981' : '#e5e7eb',
              color: selectionMode === 'manual' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Manual Selection
          </button>
          <button
            onClick={() => setSelectionMode('paper')}
            style={{
              padding: '8px 16px',
              background: selectionMode === 'paper' ? '#10b981' : '#e5e7eb',
              color: selectionMode === 'paper' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            From Question Paper
          </button>
        </div>
      </div>

      {selectionMode === 'paper' ? (
        <div style={{ marginBottom: '20px', padding: '15px', background: '#fef3c7', borderRadius: '8px' }}>
          <h4 style={{ color: '#92400e', marginBottom: '10px' }}>Select from Question Paper:</h4>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={selectedPaper}
              onChange={(e) => setSelectedPaper(e.target.value)}
              style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            >
              <option value="">Choose a Question Paper</option>
              {questionPapers.map(paper => (
                <option key={paper._id} value={paper._id}>
                  {paper.title} ({paper.subject} - Class {paper.class}) - {paper.totalQuestions} questions
                </option>
              ))}
            </select>
            <button
              onClick={handleSelectFromPaper}
              disabled={!selectedPaper || loading}
              style={{
                padding: '8px 16px',
                background: selectedPaper ? '#f59e0b' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedPaper ? 'pointer' : 'not-allowed'
              }}
            >
              {loading ? 'Loading...' : 'Select 12 Questions'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Filters for Manual Selection */}
          <div className="filters">
            <select 
              value={filters.subject} 
              onChange={(e) => setFilters({...filters, subject: e.target.value})}
            >
              <option value="">All Subjects</option>
              {subjects.map(subject => (
                <option key={subject._id} value={subject.name}>{subject.name}</option>
              ))}
            </select>
            
            <select 
              value={filters.class} 
              onChange={(e) => setFilters({...filters, class: e.target.value})}
            >
              <option value="">All Classes</option>
              {Array.from({length: 12}, (_, i) => (
                <option key={i+1} value={i+1}>Class {i+1}</option>
              ))}
            </select>
            
            <select 
              value={filters.difficulty} 
              onChange={(e) => setFilters({...filters, difficulty: e.target.value})}
            >
              <option value="">All Difficulties</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>

            <input
              type="text"
              placeholder="Chapter (optional)"
              value={filters.chapter}
              onChange={(e) => setFilters({...filters, chapter: e.target.value})}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>

          {/* Available Questions */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#000' }}>Available Questions ({availableQuestions.length})</h3>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>Loading questions...</div>
            ) : availableQuestions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                No questions found. Please adjust your filters or add questions first.
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                {availableQuestions.map((question, index) => {
                  const isSelected = selectedQuestions.find(q => q._id === question._id);
                  return (
                    <div 
                      key={question._id} 
                      style={{
                        padding: '15px',
                        borderBottom: '1px solid #f3f4f6',
                        background: isSelected ? '#ecfdf5' : 'white',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => handleQuestionToggle(question)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#000' }}>
                            {index + 1}. {question.question}
                          </div>
                          <div style={{ fontSize: '12px', color: '#000', marginBottom: '8px' }}>
                            {question.subject} - Class {question.class} - {question.difficulty}
                            {question.chapter && ` - ${question.chapter}`}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                            {question.options.map((option, optIndex) => (
                              <div 
                                key={optIndex}
                                style={{
                                  padding: '4px 8px',
                                  background: option === question.correctAnswer ? '#dcfce7' : '#f9fafb',
                                  border: option === question.correctAnswer ? '1px solid #16a34a' : '1px solid #e5e7eb',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  color: '#000'
                                }}
                              >
                                {String.fromCharCode(65 + optIndex)}: {option}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ marginLeft: '15px' }}>
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => handleQuestionToggle(question)}
                            style={{ transform: 'scale(1.2)' }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Selected Questions Summary */}
      {selectedQuestions.length > 0 && (
        <div style={{ 
          position: 'fixed', 
          bottom: '20px', 
          right: '20px', 
          background: '#1f2937', 
          color: 'white', 
          padding: '15px', 
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          minWidth: '250px'
        }}>
          <h4 style={{ margin: '0 0 10px 0' }}>Selected Questions: {selectedQuestions.length}/12</h4>
          <div style={{ fontSize: '12px', color: '#d1d5db' }}>
            {selectedQuestions.length === 12 ? 
              '✅ Ready to start KBC Multiplex Mode!' : 
              `Need ${12 - selectedQuestions.length} more questions`
            }
          </div>
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
            <button
              onClick={handleStartMultiplexGame}
              disabled={selectedQuestions.length !== 12}
              style={{
                padding: '6px 12px',
                background: selectedQuestions.length === 12 ? '#10b981' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: selectedQuestions.length === 12 ? 'pointer' : 'not-allowed'
              }}
            >
              Start Game
            </button>
            <button
              onClick={clearSelection}
              style={{
                padding: '6px 12px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherQuestionSelector;